import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Calendar, Users, MessageSquare, CheckCircle, Truck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { businessInfo } from '../mock';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    guests: '2',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast({
      title: "¡Reserva enviada!",
      description: "Te contactaremos pronto para confirmar tu reserva.",
    });
    setFormData({
      name: '',
      phone: '',
      email: '',
      date: '',
      time: '',
      guests: '2',
      message: ''
    });
  };

  return (
    <section id="contacto" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Delivery Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-[#FFB830]">🛵</span> Delivery
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {businessInfo.delivery.coverage}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-[#7CB342]/30 bg-gradient-to-br from-[#7CB342]/5 to-white">
              <CardHeader>
                <CardTitle className="flex items-center text-[#7CB342]">
                  <CheckCircle className="mr-3" size={24} />
                  Zona Vecinos - GRATIS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 font-semibold mb-2">
                  ✅ Envío gratuito en compras mayores a S/ 20.00
                </p>
                <p className="text-sm text-gray-600">
                  {businessInfo.delivery.freeZone}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#FFB830]/30 bg-gradient-to-br from-[#FFB830]/5 to-white">
              <CardHeader>
                <CardTitle className="flex items-center text-[#FFB830]">
                  <Truck className="mr-3" size={24} />
                  Resto de Pucallpa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 font-semibold mb-2">
                  🛵 Tarifa plana: S/ 5.00
                </p>
                <p className="text-sm text-gray-600">
                  Para toda la zona céntrica de Pucallpa
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-[#7CB342]">Reservas</span> y <span className="text-[#FFB830]">Contacto</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Haz tu reserva o contáctanos para pedidos especiales
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Reservation Form */}
          <Card className="border-2 border-[#7CB342]/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-[#7CB342] flex items-center">
                <Calendar className="mr-3" size={28} />
                Hacer una Reserva
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-semibold">Nombre completo *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                    placeholder="Tu nombre"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-semibold">Teléfono *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                      placeholder="123-456-7890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                      placeholder="email@ejemplo.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-gray-700 font-semibold">Fecha *</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time" className="text-gray-700 font-semibold">Hora *</Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                      className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="guests" className="text-gray-700 font-semibold">Número de personas *</Label>
                  <Input
                    id="guests"
                    name="guests"
                    type="number"
                    min="1"
                    max="20"
                    value={formData.guests}
                    onChange={handleInputChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-700 font-semibold">Mensaje (opcional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="mt-1 border-gray-300 focus:border-[#7CB342] focus:ring-[#7CB342]"
                    placeholder="Alguna petición especial..."
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#FFB830] hover:bg-[#FFA500] text-white font-bold py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <CheckCircle className="mr-2" size={20} />
                  Confirmar Reserva
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info & WhatsApp */}
          <div className="space-y-6">
            {/* WhatsApp Card */}
            <Card className="border-2 border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/5 to-white shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-[#25D366] flex items-center">
                  <MessageSquare className="mr-3" size={28} />
                  Pedidos por WhatsApp
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  ¿Prefieres pedir por WhatsApp? ¡Es rápido y fácil!
                </p>
                <Button
                  onClick={() => window.open(`https://wa.me/${businessInfo.whatsapp}`, '_blank')}
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-4 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <svg className="mr-2" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Hacer Pedido por WhatsApp
                </Button>
              </CardContent>
            </Card>

            {/* Benefits */}
            <Card className="border-2 border-[#FFB830]/20 shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800">Por qué elegirnos</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="text-[#7CB342] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Ingredientes 100% naturales y frescos</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#7CB342] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Preparación al momento</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#7CB342] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Servicio rápido y amigable</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-[#7CB342] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">Delivery disponible</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card className="border-2 border-[#7CB342]/20 shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800">Síguenos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-2 border-[#FFB830] hover:bg-[#FFB830] hover:text-white transition-colors"
                    onClick={() => window.open('https://www.facebook.com/share/1Aeez9PDAD/?mibextid=wwXIfr', '_blank')}
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-2 border-[#7CB342] hover:bg-[#7CB342] hover:text-white transition-colors"
                    onClick={() => window.open('https://www.instagram.com/lapina.pcl/', '_blank')}
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-2 border-[#FFB830] hover:bg-[#FFB830] hover:text-white transition-colors"
                    onClick={() => window.open('https://www.tiktok.com/@lapina.pcl?is_from_webapp=1&sender_device=pc', '_blank')}
                  >
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
